import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSettings } from "@/context/SettingsContext";
import { telLink } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/brands", label: "Brands" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <div className="bg-[#F5B700] text-[#111111]" data-testid="announcement-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-xs font-semibold sm:px-6">
          <p className="truncate tracking-wide">
            Industrial Electrical &amp; Automation Products | Tahliwal, Una, Himachal Pradesh
          </p>
          <a
            href={telLink(settings)}
            data-testid="announcement-call-now"
            className="flex shrink-0 items-center gap-1.5 underline-offset-2 hover:underline"
          >
            <Phone size={13} aria-hidden="true" /> Call Now
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-[#2c2c2e] bg-[#111111]/95 backdrop-blur-md" data-testid="site-header">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label="Hari Om Trading Co. — Home" data-testid="header-logo-link">
            <Logo dark />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-[#F5B700]" : "text-[#c9cdd4] hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="nav-request-quote"
              className="ml-3 bg-[#F5B700] px-5 py-2.5 text-[15px] font-semibold text-[#111111] transition-colors duration-200 hover:bg-[#D99C00]"
            >
              Request a Quote
            </Link>
          </nav>

          <button
            className="flex h-11 w-11 items-center justify-center text-white lg:hidden"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              aria-label="Mobile"
              data-testid="mobile-menu"
              className="fixed inset-x-0 top-[113px] bottom-0 z-40 overflow-y-auto bg-[#111111] lg:hidden"
              initial={reduce ? { opacity: 1 } : { opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="flex flex-col px-6 py-6">
                {NAV.map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={reduce ? false : { opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05 }}
                  >
                    <NavLink
                      to={item.to}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between border-b border-[#2c2c2e] py-4 font-heading text-2xl font-semibold ${
                          isActive ? "text-[#F5B700]" : "text-white"
                        }`
                      }
                    >
                      {item.label}
                      <span className="text-sm text-[#667085]">0{i + 1}</span>
                    </NavLink>
                  </motion.div>
                ))}
                <Link
                  to="/quote"
                  data-testid="mobile-nav-request-quote"
                  className="mt-6 bg-[#F5B700] px-5 py-4 text-center font-heading text-lg font-semibold text-[#111111]"
                >
                  Request a Quote
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
